import React, { useState, useEffect } from 'react';
import { api } from '../api';

const PLANS = [
  { id:'overnight',     label:'Overnight Only',    desc:'AI host 10pm–6am nightly', features:['AI nightly host','Voice persona','Weather + News','Session reports'] },
  { id:'overnight_wknd',label:'Overnight + Weekend',desc:'Off-peak weekdays + full weekends', features:['Everything in Overnight','Weekend full coverage','Priority voice synthesis','Email alerts'] },
  { id:'full_offhours', label:'Full Off-Hours',     desc:'All non-drive-time hours covered', features:['Everything above','Midday coverage','Show Factory (1 show/mo)','Analytics dashboard'] },
  { id:'enterprise',    label:'Enterprise (5 stations)',desc:'Multi-station group package', features:['Up to 5 stations','Dedicated account manager','Custom show formats','White-label option','API access'] },
];

export default function Billing() {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [invoices, setInvoices]       = useState([]);
  const [loading, setLoading]         = useState(true); // eslint-disable-line no-unused-vars
  const [upgrading, setUpgrading]     = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/broadcast/billing/plan').catch(() => ({ plan: null })),
      api.get('/broadcast/billing/invoices').catch(() => ({ invoices: [] })),
    ]).then(([p, inv]) => {
      setCurrentPlan(p?.plan || null);
      setInvoices(inv?.invoices || []);
    }).finally(() => setLoading(false));
  }, []);

  const selectPlan = async (planId) => {
    setUpgrading(planId);
    try {
      const d = await api.post('/broadcast/billing/subscribe', { plan_id: planId });
      if (d.checkout_url) { window.location.href = d.checkout_url; return; }
      setCurrentPlan(planId);
    } catch(e) { alert(e.message); }
    finally { setUpgrading(null); }
  };

  return (
    <div>
      {/* Current plan banner */}
      <div style={{ background: currentPlan ? 'linear-gradient(135deg,#1e1b4b,#4338ca)' : '#f8fafc', borderRadius:12, padding:'18px 22px', marginBottom:24, border: currentPlan ? 'none' : '2px dashed #e2e8f0' }}>
        {currentPlan ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ color:'rgba(255,255,255,0.6)', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Current Plan</div>
              <div style={{ color:'#fff', fontSize:18, fontWeight:800, marginTop:3 }}>{PLANS.find(p=>p.id===currentPlan)?.label || currentPlan}</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ color:'#a5b4fc', fontSize:22, fontWeight:800 }}>Contact us</div>
              <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12 }}>Next billing: —</div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'10px 0' }}>
            <div style={{ fontSize:32, marginBottom:8 }}>🎙️</div>
            <h3 style={{ margin:'0 0 4px', color:'#0f172a' }}>Choose your plan to get started</h3>
            <p style={{ margin:0, color:'#64748b', fontSize:13 }}>First 21 days free. No credit card required to start your trial.</p>
          </div>
        )}
      </div>

      {/* Plans grid */}
      <h3 style={{ margin:'0 0 14px', fontSize:15, fontWeight:700, color:'#0f172a' }}>Available Plans</h3>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14, marginBottom:28 }}>
        {PLANS.map(plan => {
          const isCurrent = currentPlan === plan.id;
          return (
            <div key={plan.id} style={{ background:'#fff', borderRadius:12, padding:20, border:`2px solid ${isCurrent ? '#6366f1' : '#f1f5f9'}`, position:'relative' }}>
              {isCurrent && <div style={{ position:'absolute', top:12, right:12, fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, background:'#eef2ff', color:'#4f46e5' }}>CURRENT</div>}
              <div style={{ fontSize:17, fontWeight:800, color:'#0f172a', marginBottom:3 }}>{plan.label}</div>
              <div style={{ fontSize:12, color:'#64748b', marginBottom:12 }}>{plan.desc}</div>
              <div style={{ fontSize:22, fontWeight:800, color:'#0f172a', marginBottom:14 }}>
                Contact us
              </div>
              <ul style={{ margin:'0 0 16px', padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:6 }}>
                {plan.features.map(f => (
                  <li key={f} style={{ display:'flex', gap:8, fontSize:12, color:'#374151', alignItems:'flex-start' }}>
                    <span style={{ color:'#22c55e', flexShrink:0 }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => selectPlan(plan.id)}
                disabled={isCurrent || upgrading === plan.id}
                style={{
                  width:'100%', padding:9, borderRadius:8, border:'none', fontSize:13, fontWeight:700, cursor: isCurrent ? 'default' : 'pointer',
                  background: isCurrent ? '#f1f5f9' : 'linear-gradient(135deg,#6366f1,#4f46e5)',
                  color: isCurrent ? '#94a3b8' : '#fff'
                }}
              >
                {upgrading === plan.id ? 'Redirecting…' : isCurrent ? 'Active Plan' : currentPlan ? 'Switch Plan' : 'Contact Groovie'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Invoices */}
      <div style={{ background:'#fff', borderRadius:12, padding:20, border:'1px solid #f1f5f9' }}>
        <h4 style={{ margin:'0 0 14px', fontSize:14, fontWeight:700, color:'#0f172a' }}>Billing History</h4>
        {invoices.length === 0 ? (
          <p style={{ fontSize:13, color:'#94a3b8', margin:0 }}>No invoices yet. Invoices will appear here after your first billing cycle.</p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {invoices.map((inv, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid #f8fafc' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0f172a' }}>{inv.description}</div>
                  <div style={{ fontSize:12, color:'#94a3b8' }}>{inv.date}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>${(inv.amount_cents/100).toFixed(2)}</span>
                  <span style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:20, background: inv.status==='paid' ? '#f0fdf4' : '#fef2f2', color: inv.status==='paid' ? '#15803d' : '#dc2626' }}>{inv.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
